import { Injectable } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { TenantService } from '../services/tenant.service';
import { Observable, map, take, catchError, of, switchMap, filter, first, timeout } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DateHelperService } from '../services/dateHelper.service';

interface SubscriptionData {
  status: string;
  isSubscribed: boolean;
  trialEndDate?: any;
  trialStartDate?: any;
  cancelAtPeriodEnd?: boolean;
  currentPeriodEnd?: any;
  currentPeriodStart?: any;
  customerId?: string;
  lastPaymentDate?: any;
  nextPaymentDate?: any;
  price?: number;
  priceId?: string;
  subscriptionId?: string;
  tierId?: string;
  upcomingInvoiceAmount?: number;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class SubscriptionGuard implements CanActivate {
  constructor(
    private router: Router,
    private afs: AngularFirestore,
    private tenantService: TenantService,
    private snackBar: MatSnackBar,
    private dateHelper: DateHelperService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    // Always allow access to billing page
    if (state.url.includes('/billing')) {
      console.log('[SubscriptionGuard] Allowing access to billing page');
      return of(true);
    }

    // Wait for tenant ID to be available (the auth guard runs first and sets it)
    return this.tenantService.tenantId$.pipe(
      filter((id): id is string => !!id),
      first(),
      timeout(10000),
      switchMap(tenantId => {
        console.log('[SubscriptionGuard] Checking access for tenant:', tenantId, 'URL:', state.url);
        console.log('[SubscriptionGuard] Fetching subscription details for tenant:', tenantId);
        console.log('[SubscriptionGuard] Full subscription path:', `${tenantId}/billing/subscriptions/current`);

        // Get subscription from modern path (billing/subscriptions/current)
        return this.afs
          .doc<SubscriptionData>(`${tenantId}/billing/subscriptions/current`)
          .valueChanges()
          .pipe(
            take(1),
        map(subscription => {
          console.log('[SubscriptionGuard] Retrieved subscription:', JSON.stringify(subscription, null, 2));
          console.log('[SubscriptionGuard] Subscription exists?', !!subscription);
          console.log('[SubscriptionGuard] Subscription type:', typeof subscription);

          // Check if subscription exists
          if (!subscription) {
            console.error('[SubscriptionGuard] ❌ No subscription document found');
            console.error('[SubscriptionGuard] ❌ Tenant ID used:', tenantId);
            console.error('[SubscriptionGuard] ❌ Path checked:', `${tenantId}/billing/subscriptions/current`);
            console.error('[SubscriptionGuard] ❌ TenantService tenant ID:', this.tenantService.getCurrentTenantId());
            this.handleInvalidSubscription('Subscription information is missing. Please contact support.');
            return false;
          }

          // Direct subscription object with status and isSubscribed
          console.log('[SubscriptionGuard] Subscription status:', subscription.status, 'isSubscribed:', subscription.isSubscribed);

          // Check if subscribed and active
          if (subscription.isSubscribed && subscription.status === 'active') {
            console.log('[SubscriptionGuard] Active subscription - allowing access');
            return true;
          }

          // Check if in trial period
          if (subscription.status === 'trial') {
            console.log('[SubscriptionGuard] Trial subscription - checking validity');
            const trialEnd = subscription.trialEndDate?.toDate ? subscription.trialEndDate?.toDate() : subscription.trialEndDate;
            if (!trialEnd) {
              console.error('[SubscriptionGuard] Missing trial end date');
              this.handleInvalidSubscription('Invalid trial period. Please contact support.');
              return false;
            }

            // Compare actual timestamps (not just dates) for precise trial expiration
            const now = new Date();
            const trialEndTime = new Date(trialEnd).getTime();
            const currentTime = now.getTime();
            const timeRemaining = trialEndTime - currentTime;

            console.log('[SubscriptionGuard] Trial end time:', trialEnd);
            console.log('[SubscriptionGuard] Current time:', now);
            console.log('[SubscriptionGuard] Time remaining (ms):', timeRemaining);
            console.log('[SubscriptionGuard] Time remaining (minutes):', Math.floor(timeRemaining / 1000 / 60));

            if (timeRemaining > 0) {
              console.log('[SubscriptionGuard] ✅ Trial is still valid - allowing access');
              return true;
            } else {
              console.log('[SubscriptionGuard] ❌ Trial has expired - blocking access');
              this.handleInvalidSubscription('Your free trial has expired. Please subscribe to continue.');
              return false;
            }
          }

          // Handle different subscription states
          console.log('[SubscriptionGuard] Handling subscription status:', subscription.status);
          switch (subscription.status) {
            case 'past_due':
              this.handleInvalidSubscription('Payment is overdue. Please update your payment method.');
              break;
            case 'canceled':
              this.handleInvalidSubscription('Subscription has been canceled. Please resubscribe to continue.');
              break;
            case 'paused':
              this.handleInvalidSubscription('Subscription is paused. Please contact support.');
              break;
            default:
              this.handleInvalidSubscription('Invalid subscription status');
          }

          console.log('[SubscriptionGuard] Blocking access due to invalid subscription status');
          return false;
        }),
        catchError(error => {
          console.error('[SubscriptionGuard] Error checking subscription:', error);
          this.handleInvalidSubscription('Error checking subscription status. Please contact support.');
          return of(false);
        })
      );
      }),
      catchError(error => {
        console.error('[SubscriptionGuard] Timeout waiting for tenant ID:', error);
        localStorage.setItem('redirectUrl', state.url);
        this.router.navigate(['/login']);
        return of(false);
      })
    );
  }

  private handleInvalidSubscription(message: string): void {
    console.log('[SubscriptionGuard] Invalid subscription:', message);
    console.log('[SubscriptionGuard] Redirecting to billing page with message');
    
    // Store the current URL before redirecting
    const currentUrl = this.router.url;
    if (currentUrl && !currentUrl.includes('/billing')) {
      localStorage.setItem('redirectUrl', currentUrl);
    }
    
    this.snackBar.open(message, 'Go to Billing', {
      duration: 5000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    }).onAction().subscribe(() => {
      this.router.navigate(['/billing']);
    });
    
    this.router.navigate(['/billing'], {
      queryParams: { message: encodeURIComponent(message) }
    });
  }
} 